import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { TrendingUp, TrendingDown, AlertTriangle, Users, Target, ArrowUpRight, ArrowDownRight } from "lucide-react";
import { useVendorComparison, useFraudPatterns } from "@/hooks/useVendorIntelligence";

interface VendorIntelligenceCardProps {
  invoices: Array<{ vendor: string; amount: number }>;
}

const formatCurrency = (amount: number) =>
  new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    maximumFractionDigits: 0,
  }).format(amount);

const formatPercent = (value: number) =>
  `${value >= 0 ? "+" : ""}${value.toFixed(1)}%`;

export function VendorIntelligenceCard({ invoices }: VendorIntelligenceCardProps) {
  const { comparisons, isLoading } = useVendorComparison(invoices);
  const { data: fraudPatterns } = useFraudPatterns();

  if (isLoading) {
    return (
      <Card className="p-6 border-border/50">
        <div className="animate-pulse space-y-4">
          <div className="h-5 bg-muted rounded w-1/3" />
          <div className="h-20 bg-muted rounded" />
        </div>
      </Card>
    );
  }

  const aboveMarket = comparisons.filter(c => c.isAboveMarket && c.differencePercent > 10);
  const belowMarket = comparisons.filter(c => !c.isAboveMarket && c.differencePercent < -10);
  const highConfidenceFraud = fraudPatterns?.filter(p => p.confidence_score >= 70) || [];

  return (
    <Card className="p-6 border-border/50 bg-gradient-to-br from-card to-card/80">
      <div className="flex items-center gap-2 mb-4">
        <Target className="h-5 w-5 text-primary" />
        <h3 className="text-lg font-bold text-foreground">Market Intelligence</h3>
        <Badge variant="outline" className="ml-auto text-xs">
          <Users className="h-3 w-3 mr-1" />
          Cross-org insights
        </Badge>
      </div>

      {comparisons.length === 0 ? (
        <p className="text-sm text-muted-foreground">
          No vendor matches found in our benchmark database yet. As more organizations use Sentinel, you'll see market comparisons here.
        </p>
      ) : (
        <div className="space-y-4">
          {/* Above Market Rate */}
          {aboveMarket.length > 0 && (
            <div className="space-y-2">
              <div className="flex items-center gap-2 text-sm font-medium text-warning">
                <ArrowUpRight className="h-4 w-4" />
                Paying Above Market ({aboveMarket.length})
              </div>
              <div className="grid gap-2">
                {aboveMarket.slice(0, 3).map((vendor) => (
                  <div
                    key={vendor.vendorName}
                    className="flex items-center justify-between p-3 rounded-lg bg-warning/10 border border-warning/20"
                  >
                    <div>
                      <p className="text-sm font-medium capitalize text-foreground">
                        {vendor.vendorName}
                      </p>
                      <p className="text-xs text-muted-foreground">
                        You pay {formatCurrency(vendor.yourAvg)} vs market avg {formatCurrency(vendor.marketAvg)}
                      </p>
                    </div>
                    <div className="text-right">
                      <Badge variant="outline" className="bg-warning/20 text-warning border-warning/30">
                        {formatPercent(vendor.differencePercent)} above
                      </Badge>
                      <p className="text-xs text-muted-foreground mt-1">
                        {vendor.sampleSize} orgs
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Below Market Rate */}
          {belowMarket.length > 0 && (
            <div className="space-y-2">
              <div className="flex items-center gap-2 text-sm font-medium text-success">
                <ArrowDownRight className="h-4 w-4" />
                Paying Below Market ({belowMarket.length})
              </div>
              <div className="grid gap-2">
                {belowMarket.slice(0, 2).map((vendor) => (
                  <div
                    key={vendor.vendorName}
                    className="flex items-center justify-between p-3 rounded-lg bg-success/10 border border-success/20"
                  >
                    <div>
                      <p className="text-sm font-medium capitalize text-foreground">
                        {vendor.vendorName}
                      </p>
                      <p className="text-xs text-muted-foreground">
                        You pay {formatCurrency(vendor.yourAvg)} vs market avg {formatCurrency(vendor.marketAvg)}
                      </p>
                    </div>
                    <Badge variant="outline" className="bg-success/20 text-success border-success/30">
                      {formatPercent(Math.abs(vendor.differencePercent))} below
                    </Badge>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Price Trends */}
          {comparisons.filter(c => c.trend !== null).length > 0 && (
            <div className="pt-2 border-t border-border/50">
              <p className="text-xs text-muted-foreground mb-2">Market Price Trends (90 days)</p>
              <div className="flex flex-wrap gap-2">
                {comparisons
                  .filter(c => c.trend !== null && Math.abs(c.trend!) > 5)
                  .slice(0, 4)
                  .map((vendor) => (
                    <Badge
                      key={vendor.vendorName}
                      variant="outline"
                      className={vendor.trend! > 0 ? "text-destructive" : "text-success"}
                    >
                      {vendor.trend! > 0 ? (
                        <TrendingUp className="h-3 w-3 mr-1" />
                      ) : (
                        <TrendingDown className="h-3 w-3 mr-1" />
                      )}
                      {vendor.vendorName}: {formatPercent(vendor.trend!)}
                    </Badge>
                  ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Fraud Corpus Alert */}
      {highConfidenceFraud.length > 0 && (
        <div className="mt-4 pt-4 border-t border-border/50">
          <div className="flex items-center gap-2 text-sm font-medium text-destructive mb-2">
            <AlertTriangle className="h-4 w-4" />
            Active Fraud Patterns ({highConfidenceFraud.length})
          </div>
          <p className="text-xs text-muted-foreground">
            Our network has detected {highConfidenceFraud.length} high-confidence fraud patterns across {highConfidenceFraud.reduce((a, p) => a + p.detection_count, 0)}+ organizations. Your transactions are automatically screened against these patterns.
          </p>
        </div>
      )}
    </Card>
  );
}
